import { useColorScheme, Dimensions, Platform, View, useWindowDimensions, ActivityIndicator, StyleSheet, ScrollView } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Surface, Icon, Text, Button, useTheme, Appbar, Banner } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { track } from '../lib/analytics';
import MapView from './mymap';
import { LocationRegion } from '../lib/types';
import Logo from './Logo';
import { WorkContextCard } from './WorkContextCard';
import type { DrivingLimitStatus, DrivingWeekLimits } from '../api/apiService';
import { DriverLimitsCard, WorkTimeStatusPanel, limitLevels } from '../features/driver/workTime';
import type { LimitKey } from '../features/driver/workTime';
import { toDriverLimits, toWorkStateSnapshot } from '../features/driver/workTime/fromApi';
import { SIDEBAR_WIDTH } from './Sidebar';
// PhoneVerificationBanner temporarily disabled — see HOTFIX below

// Use runtime window dimensions
const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

// Default location for Lisbon
const DEFAULT_LOCATION: LocationRegion = {
  latitude: 38.7223,
  longitude: -9.1393,
  latitudeDelta: 0.0922,
  longitudeDelta: 0.0421,
};

interface DashboardContentProps {
  location: LocationRegion | null;
  isSessionActive: boolean;
  isDriving: boolean;
  drivingTimeToday: string;
  drivingLimit?: DrivingLimitStatus | null;
  // Weekly total and 17-week average (ALL-136); absent on older backends.
  drivingWeekLimits?: DrivingWeekLimits | null;
  // When the current state began; drives the timer in the status card.
  stateSince?: string | null;
  actionInProgress: boolean;
  onOpenSidebar: () => void;
  onStartSession: () => void;
  onEndSession: () => void;
  onStartDriving: () => void;
  onStopDriving: () => void;
  isStartingSession: boolean;
  isEndingSession: boolean;
  isStartingDriving: boolean;
  isStoppingDriving: boolean;
  canEndSession: boolean;
  onMovementAlert?: () => void;
  onStationaryAlert?: () => void;
  trackingDegraded?: boolean;
  // Phone verification banner props
  phoneVerified?: boolean;
  phoneNumber?: string | null;
  onVerifyPhonePress?: () => void;
  onPhoneBannerDismiss?: () => void;
}

export const DashboardContent = ({
  location,
  isSessionActive,
  isDriving,
  drivingLimit,
  drivingWeekLimits,
  stateSince,
  actionInProgress,
  onOpenSidebar,
  onStartSession,
  onEndSession,
  onStartDriving,
  onStopDriving,
  isStartingSession,
  isEndingSession,
  isStartingDriving,
  isStoppingDriving,
  canEndSession,
  onMovementAlert,
  onStationaryAlert,
  trackingDegraded = false,
  phoneVerified = false,
  phoneNumber,
  onVerifyPhonePress,
  onPhoneBannerDismiss,
}: DashboardContentProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colorScheme = useColorScheme();
  const userInterfaceStyle = colorScheme === 'dark' ? 'dark' : 'light';
  const { width, height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  
  // Memoize screen size calculations to prevent unnecessary recalculations
  const isLargeScreen = useMemo(() => width > 1024, [width]);
  const isTablet = useMemo(() => width >= 768 && width <= 1024, [width]); // iPad-sized screens
  const isMobile = useMemo(() => width < 768, [width]);
  
  // Store external callbacks in refs
  const externalMovementAlertRef = useRef(onMovementAlert);
  const externalStationaryAlertRef = useRef(onStationaryAlert);
  
  // Update refs when props change
  useEffect(() => {
    externalMovementAlertRef.current = onMovementAlert;
    externalStationaryAlertRef.current = onStationaryAlert;
  }, [onMovementAlert, onStationaryAlert]);
  
  // State for alert banners
  const [showMovementAlert, setShowMovementAlert] = useState(false);
  const [showStationaryAlert, setShowStationaryAlert] = useState(false);
  const [isMapReady, setIsMapReady] = useState(Platform.OS !== 'web');
  const [mapLoadFailed, setMapLoadFailed] = useState(false);
  const [mapAttempt, setMapAttempt] = useState(0);

  const handleMapError = useCallback((error?: Error) => {
    setMapLoadFailed(true);
    track('dashboard_map_load_failed', { platform: Platform.OS, error_type: error?.message ?? 'unknown' });
  }, []);

  const retryMapLoad = useCallback(() => {
    track('dashboard_map_load_retry', { platform: Platform.OS });
    setMapLoadFailed(false);
    setIsMapReady(Platform.OS !== 'web');
    setMapAttempt((attempt) => attempt + 1);
  }, []);
  
  

  useEffect(() => {
    setIsMapReady(Platform.OS !== 'web');
  }, []);

  // Clear UI alerts when driving state changes
  useEffect(() => {
    setShowMovementAlert(false);
    setShowStationaryAlert(false);
  }, [isDriving]);
  
  // Handle movement alert
  const handleMovementAlert = useCallback(() => {
    setShowMovementAlert(true);
    // Auto-hide after 10 seconds
    setTimeout(() => {
      setShowMovementAlert(false);
    }, 10000);

    track('driver_movement_alert_banner_shown');
    
    // Call external handler if provided
    if (externalMovementAlertRef.current) {
      externalMovementAlertRef.current();
    }
  }, []);
  
  // Handle stationary alert
  const handleStationaryAlert = useCallback(() => {
    setShowStationaryAlert(true);
    // Auto-hide after 10 seconds
    setTimeout(() => {
      setShowStationaryAlert(false);
    }, 10000);

    track('driver_stationary_alert_banner_shown');
    
    // Call external handler if provided
    if (externalStationaryAlertRef.current) {
      externalStationaryAlertRef.current();
    }
  }, []);


  const renderHeader = () => {
    if (isLargeScreen) return null;
    
    // Usar altura fixa ao invés de porcentagem para evitar header muito grande
    const headerHeight = 48; // Altura compacta para mobile
    // Adicionar padding da status bar para evitar sobreposição
    const topPadding = Platform.OS === 'ios' ? insets.top : Platform.OS === 'android' ? insets.top : 0;
    
    return (
      <Appbar.Header style={{ 
        height: headerHeight + topPadding, // Incluir padding no height total
        backgroundColor: theme.colors.surface,
        position: 'absolute',
        top: 0, // Começar do topo
        left: 0,
        right: 0,
        zIndex: 1,
        alignItems: 'center',
        paddingTop: topPadding, // Adicionar padding interno
      }}>
        <Appbar.Action
          icon="menu"
          testID="driver-dashboard-open-sidebar"
          onPress={onOpenSidebar}
          color={theme.colors.onSurface}
        />
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Logo size={Platform.OS === 'web' ? 'medium' : 'mobile'} />
        </View>
        <View style={{ width: 48 }} />
      </Appbar.Header>
    );
  };

  const limits = useMemo(() => toDriverLimits(drivingLimit, drivingWeekLimits), [drivingLimit, drivingWeekLimits]);
  const workState = useMemo(
    () => toWorkStateSnapshot(isSessionActive, isDriving, stateSince),
    [isSessionActive, isDriving, stateSince],
  );
  const [limitsOpen, setLimitsOpen] = useState(false);
  const openLimits = useCallback(() => {
    setLimitsOpen(true);
    track('driver_limits_opened', { source: 'home', user_role: 'driver' });
  }, []);

  // One event each time a limit enters a warning level (no PII: the limit and the level only).
  const levels = limitLevels(limits);
  const shownLevelsRef = useRef<Partial<Record<LimitKey, string>>>({});
  const levelsKey = `${levels.rolling24h}|${levels.week}|${levels.fourMonth}`;
  useEffect(() => {
    (Object.keys(levels) as LimitKey[]).forEach((key) => {
      const level = levels[key];
      const alerting = level === 'warning' || level === 'critical' || level === 'reached';
      if (alerting && shownLevelsRef.current[key] !== level) {
        track('driver_limit_warning_shown', { limit: key, level, source: 'home', user_role: 'driver' });
      }
      shownLevelsRef.current[key] = level;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelsKey]);

  const contentMaxWidth = useMemo(() => {
    if (isLargeScreen) {
      const availableWidth = width - SIDEBAR_WIDTH;
      return Math.min(800, availableWidth * 0.7);
    }
    if (isTablet) {
      return Math.min(600, width * 0.8);
    }
    return Math.min(400, width * 0.9);
  }, [width, isLargeScreen, isTablet]);

  // Calcular height total do header incluindo status bar
  const totalHeaderHeight = useMemo(() => {
    if (isLargeScreen) return 0;
    const headerHeight = 48; // Altura compacta para mobile
    const statusBarPadding = Platform.OS === 'ios' ? insets.top : Platform.OS === 'android' ? insets.top : 0;
    return headerHeight + statusBarPadding;
  }, [isLargeScreen, insets.top]);

  return (
    <View style={{ flex: 1, backgroundColor: theme.colors.background }}>
      {renderHeader()}
      
      {/* Alert Banners - Flexbox centering (no manual left/translateX) */}
      <View style={{ 
        position: 'absolute',
        top: isLargeScreen ? 16 : totalHeaderHeight + 16,
        left: 0,
        right: 0,
        zIndex: 10,
        alignItems: 'center',
      }}>
        <View style={{ width: contentMaxWidth, maxWidth: '100%' }}>
        {trackingDegraded && (
          <Banner
            visible={true}
            testID="driver-tracking-degraded"
            icon="map-marker-off"
            style={{
              backgroundColor: theme.colors.secondaryContainer,
              marginBottom: 8,
              borderRadius: 8,
              elevation: 4,
            }}
          >
            <Text style={{ color: theme.colors.onSecondaryContainer }}>
              {t('alerts.locationTrackingDegraded')}
            </Text>
          </Banner>
        )}
        {showMovementAlert && (
          <Banner
            visible={true}
            icon="car-off"
            actions={[
              { label: t('startDriving'), onPress: () => { track('driver_alert_banner_action', { alert_type: 'movement', action: 'start_driving' }); onStartDriving(); setShowMovementAlert(false); } },
              { label: t('dismiss'), onPress: () => { track('driver_alert_banner_dismissed', { alert_type: 'movement' }); setShowMovementAlert(false); } }
            ]}
            style={{
              backgroundColor: theme.colors.errorContainer,
              marginBottom: 8,
              borderRadius: 8,
              elevation: 4,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.2,
              shadowRadius: 3,
            }}
          >
            <Text style={{ color: theme.colors.onErrorContainer, fontWeight: 'bold' }}>
              {t('alerts.movementDetected')}
            </Text>
          </Banner>
        )}
        
        {showStationaryAlert && (
          <Banner
            visible={true}
            icon="car-brake-hold"
            actions={[
              { label: t('stopDriving'), onPress: () => { track('driver_alert_banner_action', { alert_type: 'stationary', action: 'stop_driving' }); onStopDriving(); setShowStationaryAlert(false); } },
              { label: t('dismiss'), onPress: () => { track('driver_alert_banner_dismissed', { alert_type: 'stationary' }); setShowStationaryAlert(false); } }
            ]}
            style={{
              backgroundColor: theme.colors.surfaceVariant,
              marginBottom: 8,
              borderRadius: 8,
              elevation: 4,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.2,
              shadowRadius: 3,
            }}
          >
            <Text style={{ color: theme.colors.onSurfaceVariant, fontWeight: 'bold' }}>
              {t('alerts.stationaryDetected')}
            </Text>
          </Banner>
        )}
        </View>
      </View>

      {/* HOTFIX: Phone Verification Banner temporarily disabled until migration is stable in production */}
      
      {/* Main Content */}
      <View style={{ 
        flex: 1,
        marginTop: isLargeScreen ? 0 : totalHeaderHeight,
      }}>
        {/* Map Background */}
        <View style={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: theme.colors.surfaceVariant,
        }}>
          <MapView
            key={`dashboard-map-${mapAttempt}`}
            style={{ 
              flex: 1,
              width: '100%',
              height: '100%',
            }}
            provider={Platform.OS === 'android' ? 'google' : undefined}
            region={location || DEFAULT_LOCATION}
            showsUserLocation
            userInterfaceStyle={userInterfaceStyle}
            showsCompass={false}
            showsMyLocationButton={false}
            onMapReady={() => setIsMapReady(true)}
            onMapError={handleMapError}
          />

          {!isMapReady && !mapLoadFailed && (
            <View
              testID="dashboard-map-loading"
              pointerEvents="none"
              style={{
                ...StyleSheet.absoluteFillObject,
                justifyContent: 'space-between',
                backgroundColor: colorScheme === 'dark' ? 'rgba(17, 24, 39, 0.72)' : 'rgba(241, 245, 249, 0.88)',
                paddingHorizontal: isMobile ? 20 : 28,
                paddingVertical: isMobile ? 28 : 36,
              }}
            >
              <View style={{ gap: 12 }}>
                <View style={{ flexDirection: 'row', gap: 10 }}>
                  <View style={{
                    height: 12,
                    width: isMobile ? '42%' : 180,
                    borderRadius: 999,
                    backgroundColor: theme.colors.outlineVariant,
                    opacity: 0.55,
                  }} />
                  <View style={{
                    height: 12,
                    width: isMobile ? '24%' : 110,
                    borderRadius: 999,
                    backgroundColor: theme.colors.outlineVariant,
                    opacity: 0.35,
                  }} />
                </View>
                <View style={{
                  height: 96,
                  borderRadius: 24,
                  backgroundColor: colorScheme === 'dark' ? 'rgba(51, 65, 85, 0.42)' : 'rgba(148, 163, 184, 0.18)',
                  borderWidth: 1,
                  borderColor: colorScheme === 'dark' ? 'rgba(148, 163, 184, 0.26)' : 'rgba(100, 116, 139, 0.18)',
                }} />
              </View>

              <View style={{ alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                <Surface
                  elevation={2}
                  style={{
                    width: '100%',
                    maxWidth: isMobile ? 320 : 380,
                    paddingHorizontal: 18,
                    paddingVertical: 18,
                    borderRadius: 24,
                    backgroundColor: theme.colors.surface,
                    borderWidth: 1,
                    borderColor: theme.colors.outlineVariant,
                    gap: 14,
                  }}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                    <View style={{
                      width: 44,
                      height: 44,
                      borderRadius: 22,
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: theme.colors.primaryContainer,
                    }}>
                      <Icon source="map-marker-radius" size={22} color={theme.colors.primary} />
                    </View>
                    <View style={{ flex: 1, gap: 4 }}>
                      <Text variant={isMobile ? 'titleMedium' : 'titleLarge'} style={{ color: theme.colors.onSurface }}>
                        {t('mapLoading.title', 'Preparing your live map')}
                      </Text>
                      <Text style={{ color: theme.colors.onSurfaceVariant }}>
                        {t('mapLoading.subtitle', 'Loading location view and controls before the route appears.')}
                      </Text>
                    </View>
                  </View>

                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <ActivityIndicator size="small" color={theme.colors.primary} />
                    <Text style={{ color: theme.colors.onSurfaceVariant }}>{t('mapLoading.loading', 'Loading map…')}</Text>
                  </View>

                  <View style={{ gap: 8 }}>
                    <View style={{
                      height: 10,
                      width: '100%',
                      borderRadius: 999,
                      backgroundColor: theme.colors.outlineVariant,
                      opacity: 0.5,
                    }} />
                    <View style={{
                      height: 10,
                      width: '72%',
                      borderRadius: 999,
                      backgroundColor: theme.colors.outlineVariant,
                      opacity: 0.32,
                    }} />
                  </View>
                </Surface>
              </View>

              <View style={{ alignItems: 'flex-end', gap: 10 }}>
                <View style={{
                  width: isMobile ? 140 : 180,
                  height: 14,
                  borderRadius: 999,
                  backgroundColor: theme.colors.outlineVariant,
                  opacity: 0.42,
                }} />
                <View style={{
                  width: isMobile ? 180 : 240,
                  height: 72,
                  borderRadius: 20,
                  backgroundColor: colorScheme === 'dark' ? 'rgba(51, 65, 85, 0.34)' : 'rgba(148, 163, 184, 0.16)',
                  borderWidth: 1,
                  borderColor: colorScheme === 'dark' ? 'rgba(148, 163, 184, 0.24)' : 'rgba(100, 116, 139, 0.14)',
                }} />
              </View>
            </View>
          )}

          {mapLoadFailed && (
            <View
              testID="dashboard-map-error"
              style={{
                ...StyleSheet.absoluteFillObject,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: colorScheme === 'dark' ? 'rgba(17, 24, 39, 0.72)' : 'rgba(241, 245, 249, 0.88)',
                paddingHorizontal: isMobile ? 20 : 28,
              }}
            >
              <Surface
                elevation={2}
                style={{
                  width: '100%',
                  maxWidth: isMobile ? 320 : 380,
                  padding: 18,
                  borderRadius: 24,
                  backgroundColor: theme.colors.surface,
                  borderWidth: 1,
                  borderColor: theme.colors.outlineVariant,
                  gap: 14,
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                  <Icon source="map-marker-off" size={22} color={theme.colors.error} />
                  <View style={{ flex: 1, gap: 4 }}>
                    <Text variant={isMobile ? 'titleMedium' : 'titleLarge'} style={{ color: theme.colors.onSurface }}>
                      {t('mapLoading.errorTitle', "The map couldn't load")}
                    </Text>
                    <Text style={{ color: theme.colors.onSurfaceVariant }}>
                      {t('mapLoading.errorMessage', 'Check your connection and try again. Session controls keep working.')}
                    </Text>
                  </View>
                </View>
                <Button testID="dashboard-map-retry" mode="contained" icon="refresh" onPress={retryMapLoad}>
                  {t('mapLoading.retry', 'Try again')}
                </Button>
              </Surface>
            </View>
          )}
        </View>

        {/* Status Surface - Flexbox centering (no manual left/translateX) */}
        <View style={{
          position: 'absolute',
          top: 16,
          left: 0,
          right: 0,
          alignItems: 'center',
          zIndex: 1,
        }}>
        <Surface
          elevation={4}
          style={{
            padding: isTablet ? 20 : 16,
            borderRadius: 16,
            backgroundColor: theme.colors.surface,
            gap: 12,
            width: contentMaxWidth,
            maxWidth: isLargeScreen ? 800 : isTablet ? 600 : 400,
            borderWidth: 1,
            borderColor: theme.colors.outline,
          }}
        >
          <WorkTimeStatusPanel
            snapshot={workState}
            limits={limits}
            onOpenLimits={openLimits}
            large={isTablet}
          />
          <WorkContextCard sessionActive={isSessionActive} />
        </Surface>
        </View>

        {limitsOpen ? (
          <View
            testID="driver-limits-overlay"
            style={{
              ...StyleSheet.absoluteFillObject,
              backgroundColor: theme.colors.backdrop,
              zIndex: 3,
            }}
          >
            <ScrollView contentContainerStyle={{ alignItems: 'center', padding: 16 }}>
              <View style={{ width: contentMaxWidth, maxWidth: '100%' }}>
                <DriverLimitsCard limits={limits} onClose={() => setLimitsOpen(false)} />
              </View>
            </ScrollView>
          </View>
        ) : null}

        {/* Buttons Container */}
        <View style={{
          position: 'absolute',
          bottom: 0,
          // Parent surface already accounts for sidebar margin; no extra offset here
          left: 0,
          right: 0,
          backgroundColor: colorScheme === 'dark' ? 'rgba(32, 32, 32, 0.8)' : 'rgba(255, 255, 255, 0.8)',
          borderTopWidth: 1,
          borderTopColor: theme.colors.outline,
          zIndex: 2,
        }}>
          {(isLargeScreen || isTablet) ? (
            <View style={{ 
              padding: 16,
              width: contentMaxWidth,
              alignSelf: 'center',
            }}>
              <View style={{ width: '100%', gap: isTablet ? 12 : 10 }}>
                {!isSessionActive ? (
                  <Button
                    mode="contained"
                    onPress={onStartSession}
                    disabled={actionInProgress || isStartingSession}
                    style={{ 
                      borderRadius: 16, 
                      backgroundColor: theme.colors.tertiary,
                      width: '100%',
                    }}
                    labelStyle={{ 
                      paddingVertical: isTablet ? 12 : 8, 
                      fontSize: isTablet ? 18 : 16,
                      color: theme.colors.onTertiary,
                    }}
                    contentStyle={{ paddingHorizontal: 16 }}
                  >
                    {t('startSession')}
                  </Button>
                ) : (
                  <View style={{ 
                    flexDirection: isTablet ? 'column' : 'row', 
                    gap: isTablet ? 12 : 10 
                  }}>
                    <Button
                      mode="contained"
                      onPress={onEndSession}
                      disabled={actionInProgress || isEndingSession || !canEndSession}
                      style={{ 
                        flex: isTablet ? 0 : 1,
                        borderRadius: 16, 
                        backgroundColor: theme.colors.error,
                        opacity: canEndSession ? 1 : 0.5,
                        width: isTablet ? '100%' : undefined,
                      }}
                      labelStyle={{ 
                        paddingVertical: isTablet ? 12 : 8, 
                        fontSize: isTablet ? 18 : 16,
                        color: theme.colors.onError,
                      }}
                      contentStyle={{ paddingHorizontal: 16 }}
                    >
                      {t('endSession')}
                    </Button>
                    <Button
                      mode="contained"
                      onPress={isDriving ? onStopDriving : onStartDriving}
                      disabled={actionInProgress || isStartingDriving || isStoppingDriving || isEndingSession}
                      style={{ 
                        flex: isTablet ? 0 : 1,
                        borderRadius: 16, 
                        backgroundColor: actionInProgress || isStartingDriving || isStoppingDriving || isEndingSession
                          ? theme.colors.surfaceVariant 
                          : theme.colors.tertiary,
                        width: isTablet ? '100%' : undefined,
                        opacity: actionInProgress || isStartingDriving || isStoppingDriving || isEndingSession ? 0.7 : 1,
                      }}
                      labelStyle={{ 
                        paddingVertical: isTablet ? 12 : 8, 
                        fontSize: isTablet ? 18 : 16,
                        color: actionInProgress || isStartingDriving || isStoppingDriving || isEndingSession
                          ? theme.colors.onSurfaceVariant 
                          : theme.colors.onTertiary,
                      }}
                      contentStyle={{ 
                        paddingHorizontal: 16,
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                      }}
                    >
                      {actionInProgress || isStartingDriving || isStoppingDriving || isEndingSession ? (
                        <>
                          <ActivityIndicator 
                            size="small" 
                            color={theme.colors.onSurfaceVariant} 
                          />
                          <Text style={{ 
                            color: theme.colors.onSurfaceVariant,
                            fontSize: isTablet ? 18 : 16,
                          }}>
                            {isStartingDriving ? t('drivingStates.starting', 'Starting...') : isStoppingDriving ? t('drivingStates.stopping', 'Stopping...') : isEndingSession ? t('drivingStates.endingSession', 'Ending Session...') : t('drivingStates.processing', 'Processing...')}
                          </Text>
                        </>
                      ) : (
                        isDriving ? t('stopDriving') : t('startDriving')
                      )}
                    </Button>
                  </View>
                )}
              </View>
            </View>
          ) : (
            <SafeAreaView>
              <View style={{ padding: 16 }}>
                <View style={{ width: '100%', gap: 10 }}>
                  {!isSessionActive ? (
                    <Button
                      mode="contained"
                      onPress={onStartSession}
                      disabled={actionInProgress || isStartingSession}
                      style={{ 
                        borderRadius: 16, 
                        backgroundColor: theme.colors.tertiary,
                        width: '100%',
                      }}
                      labelStyle={{ 
                        paddingVertical: 8, 
                        fontSize: 16,
                        color: theme.colors.onTertiary,
                      }}
                      contentStyle={{ paddingHorizontal: 16 }}
                    >
                      {t('startSession')}
                    </Button>
                  ) : (
                    <>
                      <Button
                        mode="contained"
                        onPress={onEndSession}
                        disabled={actionInProgress || isEndingSession || !canEndSession}
                        style={{ 
                          borderRadius: 16, 
                          backgroundColor: theme.colors.error, 
                          width: '100%',
                          opacity: canEndSession ? 1 : 0.5,
                        }}
                        labelStyle={{ 
                          paddingVertical: 8, 
                          fontSize: 16,
                          color: theme.colors.onError,
                        }}
                        contentStyle={{ paddingHorizontal: 16 }}
                      >
                        {t('endSession')}
                      </Button>
                      <Button
                        mode="contained"
                        onPress={isDriving ? onStopDriving : onStartDriving}
                        disabled={actionInProgress || isStartingDriving || isStoppingDriving || isEndingSession}
                        style={{ 
                          borderRadius: 16, 
                          backgroundColor: actionInProgress || isStartingDriving || isStoppingDriving 
                            ? theme.colors.surfaceVariant 
                            : theme.colors.tertiary,
                          width: '100%',
                          opacity: actionInProgress || isStartingDriving || isStoppingDriving || isEndingSession ? 0.7 : 1,
                        }}
                        labelStyle={{ 
                          paddingVertical: 8, 
                          fontSize: 16,
                          color: actionInProgress || isStartingDriving || isStoppingDriving 
                            ? theme.colors.onSurfaceVariant 
                            : theme.colors.onTertiary,
                        }}
                        contentStyle={{ 
                          paddingHorizontal: 16,
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8,
                        }}
                      >
                        {actionInProgress || isStartingDriving || isStoppingDriving || isEndingSession ? (
                          <>
                            <ActivityIndicator 
                              size="small" 
                              color={theme.colors.onSurfaceVariant} 
                            />
                            <Text style={{ 
                              color: theme.colors.onSurfaceVariant,
                              fontSize: 16,
                            }}>
                              {isStartingDriving ? t('drivingStates.starting', 'Starting...') : isStoppingDriving ? t('drivingStates.stopping', 'Stopping...') : isEndingSession ? t('drivingStates.endingSession', 'Ending Session...') : t('drivingStates.processing', 'Processing...')}
                            </Text>
                          </>
                        ) : (
                          isDriving ? t('stopDriving') : t('startDriving')
                        )}
                      </Button>
                    </>
                  )}
                </View>
              </View>
            </SafeAreaView>
          )}
        </View>
      </View>
      
    </View>
  );
};
